/**
 * main.ts
 * BlackBox Dungeon Master — V1 App Bootstrap
 *
 * Initializes the Babylon.js engine, creates a demo octile grid,
 * and renders it in 3D. Handles splash → app transition.
 * Phase 3: Editor pipeline with mode routing and wall editing.
 */

import { Vector3 } from '@babylonjs/core'
import { Engine } from './core/Engine.ts'
import { GridRenderer } from './grid/GridRenderer.ts'
import { getActiveOctagons } from './grid/OctileGrid.ts'
import { gridToWorld } from './grid/GridAddressing.ts'
import { detectMode } from './editor/ModeDetector.ts'
import { GridView, MODE_LABELS, MODE_TIPS } from './editor/GridView.ts'
import { WallTool } from './editor/WallTool.ts'
import { CellTypeTool } from './editor/CellTypeTool.ts'
import { ContentTool } from './editor/ContentTool.ts'
import { AutoGenerator } from './editor/AutoGenerator.ts'
import { RoomCluster } from './editor/RoomCluster.ts'
import { Minimap } from './panels/Minimap.ts'
import { EnergyMeter } from './panels/EnergyMeter.ts'
import { DungeonConfig } from './panels/DungeonConfig.ts'
import { ValidatorPanel } from './panels/ValidatorPanel.ts'
import { exportBBD, importBBD, importBBDFromJSON } from './io/DungeonIO.ts'
import { WorldBridge } from './io/WorldBridge.ts'
import { countActiveCells } from './grid/OctileGrid.ts'
import type { LayoutArchetype } from './editor/AutoGenerator.ts'
import type { RoomType } from './editor/RoomCluster.ts'
import type { DungeonPersonality } from './types/GrowthTypes.ts'
import type { OctileDungeon, GridPos, CellType } from './types/OctileTypes.ts'
import type { EditorMode } from './editor/GridView.ts'

class DungeonMasterApp {
    private engine!: Engine
    private renderer!: GridRenderer
    private gridView!: GridView
    private wallTool: WallTool | null = null
    private cellTypeTool!: CellTypeTool
    private contentTool: ContentTool | null = null
    private roomCluster: RoomCluster | null = null
    private minimap: Minimap | null = null
    private energyMeter: EnergyMeter | null = null
    private dungeonConfig: DungeonConfig | null = null
    private validatorPanel: ValidatorPanel | null = null
    private autoGenerator = new AutoGenerator()
    private worldBridge: WorldBridge | null = null
    private dungeon: OctileDungeon | null = null
    private pendingRoomCells: GridPos[] = []
    private userSetSeed = false

    async init(): Promise<void> {
        const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement
        if (!canvas) throw new Error('Canvas #renderCanvas not found')

        // Detect app context
        const context = detectMode()
        console.log(`Dungeon Master mode: ${context.mode}`)

        // Initialize WorldBridge for iframe communication
        this.worldBridge = new WorldBridge(context, {
            onLoadDungeon: (bbdJson) => {
                try {
                    const dungeon = importBBDFromJSON(bbdJson)
                    this.loadDungeon(dungeon)
                    this.updateStatus(`Loaded "${dungeon.name}" from World`)
                } catch (err) {
                    this.updateStatus(`Failed to load dungeon: ${(err as Error).message}`)
                }
            },
        })

        // Adapt UI for embedded mode
        if (this.worldBridge.isEmbedded()) {
            this.setupEmbeddedUI()
        }

        this.engine = new Engine(canvas)
        this.renderer = new GridRenderer(this.engine.scene)

        // Initialize CellTypeTool (properties panel)
        const propsPanel = document.getElementById('propertiesPanel')!
        this.cellTypeTool = new CellTypeTool([], this.renderer, propsPanel)

        // Initialize ContentTool (content palette + placement)
        const paletteContainer = document.getElementById('contentPaletteContainer')
        if (paletteContainer) {
            this.contentTool = new ContentTool(
                [],
                paletteContainer,
                () => {
                    if (this.dungeon) {
                        this.energyMeter?.recalculateFromGrid(this.dungeon.layers[0].grid)
                    }
                },
                (msg) => this.updateStatus(msg),
                () => {
                    // Re-render Properties content section when palette selection changes
                    const sel = this.gridView?.getSelection()
                    if (sel?.length === 1) {
                        this.cellTypeTool.showCell(sel[0].row, sel[0].col)
                    }
                },
            )
            this.contentTool.renderPalette()
            this.cellTypeTool.setContentTool(this.contentTool)
        }

        // Initialize Minimap
        const minimapCanvas = document.getElementById('minimapCanvas') as HTMLCanvasElement
        if (minimapCanvas) {
            this.minimap = new Minimap(minimapCanvas, {
                onCellClicked: (row, col) => {
                    this.gridView.selectCell(row, col)
                },
            })
        }

        // Initialize EnergyMeter
        const energyContainer = document.getElementById('energyMeterContainer')
        if (energyContainer) {
            this.energyMeter = new EnergyMeter(energyContainer, 1000)
        }

        // Initialize DungeonConfig panel
        const dungeonInfoEl = document.getElementById('dungeonInfo')
        if (dungeonInfoEl) {
            this.dungeonConfig = new DungeonConfig(dungeonInfoEl, {
                onNameChanged: (name) => this.updateStatus(`Dungeon renamed to "${name}"`),
                onCorridorScaleChanged: () => {
                    if (this.dungeon) {
                        this.renderer.renderGrid(
                            this.dungeon.layers[0].grid,
                            this.dungeon.baseSquareSize,
                            this.dungeon.cornerCutRatio
                        )
                        this.updateStatus(`Corridor scale: ${this.dungeon.corridorScale}`)
                    }
                },
            })
        }

        // Initialize Validator panel
        const validatorEl = document.getElementById('validatorPanel')
        if (validatorEl) {
            this.validatorPanel = new ValidatorPanel(validatorEl, {
                onCellsHighlighted: (cells) => {
                    this.renderer.deselectAll()
                    for (const c of cells) this.renderer.selectCell(c.row, c.col)
                    this.minimap?.setSelection(cells)
                    this.updateStatus(`${cells.length} cell${cells.length > 1 ? 's' : ''} highlighted`)
                },
            })
        }

        // Initialize GridView (central pointer router)
        this.gridView = new GridView(this.engine, this.renderer, {
            onCellSelected: (row, col) => {
                // In Content mode with a selected palette item, auto-place on click
                if (this.gridView.getMode() === 'content' && this.contentTool?.getSelectedItem()) {
                    const item = this.contentTool.getSelectedItem()!
                    if (this.contentTool.placeContent(row, col, item)) {
                        this.energyMeter?.recalculateFromGrid(this.dungeon!.layers[0].grid)
                    }
                }
                this.cellTypeTool.showCell(row, col)
                this.focusPropertiesPanel(true)
                this.minimap?.setSelection([{ row, col }])
                this.updateStatus(`Selected cell [${row}, ${col}]`)
            },
            onCellDeselected: () => {
                this.cellTypeTool.clear()
                this.focusPropertiesPanel(false)
                this.minimap?.clearSelection()
                this.updateStatus('Selection cleared')
            },
            onWallClicked: (row, col, wallIndex) => {
                this.handleWallClick(row, col, wallIndex)
            },
            onMultiSelectChanged: (cells) => {
                this.pendingRoomCells = cells
                this.updateCreateRoomButton(cells)
                this.minimap?.setSelection(cells)
                this.updateStatus(`${cells.length} cells selected`)
            },
            onModeChanged: (mode) => {
                this.updateModeIndicator(mode)
            },
        })

        this.bindUI()
        this.bindKeyboard()
        this.updateModeIndicator(this.gridView.getMode())
        this.updateStatus('Ready. Click "Generate" to create an octile dungeon.')
    }

    // ─── Wall Editing ──────────────────────────────────

    private handleWallClick(row: number, col: number, wallIndex: number): void {
        if (!this.wallTool) return

        const result = this.wallTool.cycleWall(row, col, wallIndex)
        if (!result) return

        // Update visual for the clicked wall
        this.renderer.updateWall(row, col, wallIndex, result.newState)

        // Update reciprocal wall visual
        if (result.reciprocal) {
            const r = result.reciprocal
            this.renderer.updateWall(r.row, r.col, r.wallIndex, result.newState)
        }

        this.minimap?.render()
        const wallName = WallTool.getWallName(row, col, wallIndex)
        this.updateStatus(`Wall [${row},${col}] ${wallName}: → ${result.newState}`)
    }

    // ─── UI Bindings ───────────────────────────────────

    private bindUI(): void {
        document.getElementById('btnDemo')?.addEventListener('click', () => this.generateDemo())
        document.getElementById('btnGenerate')?.addEventListener('click', () => this.generateDemo())
        document.getElementById('btnTopDown')?.addEventListener('click', () => this.engine.setTopDown())
        document.getElementById('btnPerspective')?.addEventListener('click', () => this.engine.setPerspective())
        document.getElementById('btnWireframe')?.addEventListener('click', () => {
            const btn = document.getElementById('btnWireframe') as HTMLButtonElement
            const active = btn.dataset.active === 'true'
            btn.dataset.active = active ? 'false' : 'true'
            btn.classList.toggle('active', !active)
            this.engine.scene.meshes.forEach(m => {
                if (m.material) m.material.wireframe = !active
            })
        })

        // Slider value displays
        document.getElementById('cellCount')?.addEventListener('input', (e) => {
            const display = document.getElementById('cellCountValue')
            if (display) display.textContent = (e.target as HTMLInputElement).value
        })
        document.getElementById('branching')?.addEventListener('input', (e) => {
            const display = document.getElementById('branchingValue')
            if (display) display.textContent = (e.target as HTMLInputElement).value + '%'
        })
        document.getElementById('difficulty')?.addEventListener('input', (e) => {
            const display = document.getElementById('difficultyValue')
            if (display) display.textContent = (e.target as HTMLInputElement).value
        })

        // Seed input — mark as user-set when manually edited
        document.getElementById('seedInput')?.addEventListener('input', () => {
            this.userSetSeed = true
        })
        document.getElementById('seedInput')?.addEventListener('change', () => {
            this.userSetSeed = true
            if (this.dungeon) this.generateDemo()
        })

        // Export / Import
        document.getElementById('btnExport')?.addEventListener('click', () => this.handleExport())
        document.getElementById('btnImport')?.addEventListener('click', () => {
            (document.getElementById('fileImport') as HTMLInputElement)?.click()
        })
        document.getElementById('fileImport')?.addEventListener('change', (e) => {
            const file = (e.target as HTMLInputElement).files?.[0]
            if (file) this.handleImport(file)
        })

        // Room creation
        document.getElementById('btnCreateRoom')?.addEventListener('click', () => this.openRoomDialog())
        document.getElementById('roomDialogCancel')?.addEventListener('click', () => {
            (document.getElementById('roomDialog') as HTMLDialogElement)?.close()
        })
        document.getElementById('roomDialogCreate')?.addEventListener('click', () => this.handleCreateRoom())

        // Enter key in dialog name field → create
        document.getElementById('roomDialogName')?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                this.handleCreateRoom()
            }
        })

        // Legend → cell type quick-assign
        document.querySelectorAll('.legend-item[data-cell-type]').forEach(item => {
            item.addEventListener('click', () => {
                const type = (item as HTMLElement).dataset.cellType as CellType
                const sel = this.gridView.getSelection()
                if (sel.length === 0) {
                    this.updateStatus('Select a cell first, then click a room type')
                    return
                }
                for (const { row, col } of sel) {
                    this.cellTypeTool.setCellType(row, col, type)
                }
                this.minimap?.render()
                this.updateStatus(`Set ${sel.length} cell(s) to ${type.replace(/_/g, ' ')}`)
            })
        })

        // Mode guide dismiss
        document.getElementById('modeGuideDismiss')?.addEventListener('click', () => {
            const section = document.getElementById('modeGuideSection')
            if (section) {
                section.classList.add('dismissed')
                section.style.display = 'none'
            }
        })
    }

    private bindKeyboard(): void {
        window.addEventListener('keydown', (e) => {
            // Don't handle keys when a dialog is open or typing in inputs
            const dialog = document.getElementById('roomDialog') as HTMLDialogElement
            if (dialog?.open) return
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return

            // Spacebar cycles editor mode
            if (e.code === 'Space') {
                e.preventDefault()
                this.gridView.cycleMode()
            }
        })
    }

    private updateModeIndicator(mode: EditorMode): void {
        const labelEl = document.getElementById('modeLabel')
        const indicatorEl = document.getElementById('modeIndicator')
        if (labelEl) labelEl.textContent = MODE_LABELS[mode]
        if (indicatorEl) indicatorEl.dataset.mode = mode

        // Update Mode Guide panel
        const guideSection = document.getElementById('modeGuideSection')
        if (guideSection && !guideSection.classList.contains('dismissed')) {
            const titleEl = document.getElementById('modeGuideTitle')
            const tipsEl = document.getElementById('modeGuideTips')
            if (titleEl) titleEl.textContent = `${MODE_LABELS[mode]} Mode`
            if (tipsEl) {
                tipsEl.innerHTML = MODE_TIPS[mode]
                    .map(tip => `<li>${tip}</li>`)
                    .join('')
            }
        }
    }

    // ─── Dungeon Generation ────────────────────────────

    generateDemo(): void {
        const seedInput = document.getElementById('seedInput') as HTMLInputElement
        const archetypeSelect = document.getElementById('archetypeSelect') as HTMLSelectElement
        const personalitySelect = document.getElementById('personalitySelect') as HTMLSelectElement
        const cellCountSlider = document.getElementById('cellCount') as HTMLInputElement
        const branchingSlider = document.getElementById('branching') as HTMLInputElement
        const difficultySlider = document.getElementById('difficulty') as HTMLInputElement

        // Use user's seed if they set one, otherwise spin a fresh random seed
        let seed: number
        if (this.userSetSeed && seedInput?.value) {
            seed = parseInt(seedInput.value) || Date.now()
        } else {
            seed = Math.floor(Math.random() * 1_000_000)
            if (seedInput) seedInput.value = String(seed)
        }
        const branchingVal = parseInt(branchingSlider?.value || '40') / 100

        this.updateStatus('Generating dungeon...')

        const result = this.autoGenerator.generate({
            archetype: (archetypeSelect?.value || 'branching') as LayoutArchetype,
            personality: (personalitySelect?.value || 'expansive') as DungeonPersonality,
            targetCells: parseInt(cellCountSlider?.value || '18'),
            difficulty: parseInt(difficultySlider?.value || '5'),
            seed,
            loopProbability: branchingVal,
            branchProbability: branchingVal,
        })

        this.loadDungeon(result.dungeon)

        const s = result.stats
        this.updateStatus(
            `${s.activatedOctagons} rooms, ${this.dungeon!.dna.personality} ${(archetypeSelect?.value || 'branching')}, ` +
            `seed: ${seed}, ${s.isFullyConnected ? 'fully connected' : 'WARNING: disconnected!'}`
        )
        this.updateDungeonInfo(s.activatedOctagons)
    }

    // ─── Export / Import ─────────────────────────────

    private handleExport(): void {
        if (!this.dungeon) {
            this.updateStatus('No dungeon to export — generate one first')
            return
        }

        // In embedded mode, send to World instead of downloading
        if (this.worldBridge?.isEmbedded()) {
            this.worldBridge.saveAndClose(this.dungeon)
            this.updateStatus(`Saved "${this.dungeon.name}" to World`)
            return
        }

        exportBBD(this.dungeon)
        this.updateStatus(`Exported "${this.dungeon.name}.bbd"`)
    }

    /**
     * Adapt UI when running inside World's iframe modal.
     * Swap "Export" for "Save to World", hide standalone-only elements.
     */
    private setupEmbeddedUI(): void {
        // Rename Export button
        const btnExport = document.getElementById('btnExport')
        if (btnExport) {
            btnExport.textContent = '💾 Save to World'
            btnExport.title = 'Save dungeon to your world and close'
        }

        // Hide Import button (World handles loading via postMessage)
        const btnImport = document.getElementById('btnImport')
        if (btnImport) btnImport.style.display = 'none'
    }

    private async handleImport(file: File): Promise<void> {
        try {
            this.updateStatus('Importing dungeon...')
            const dungeon = await importBBD(file)
            this.loadDungeon(dungeon)
            this.updateStatus(`Imported "${dungeon.name}" — ${countActiveCells(dungeon.layers[0].grid).octagons} rooms`)
        } catch (err) {
            this.updateStatus(`Import failed: ${(err as Error).message}`)
        }
        // Reset file input so re-importing same file triggers change event
        const input = document.getElementById('fileImport') as HTMLInputElement
        if (input) input.value = ''
    }

    private loadDungeon(dungeon: OctileDungeon): void {
        this.dungeon = dungeon
        const grid = dungeon.layers[0].grid

        this.renderer.renderGrid(grid, dungeon.baseSquareSize, dungeon.cornerCutRatio)
        this.wallTool = new WallTool(grid, dungeon.gridWidth, dungeon.gridHeight)
        this.roomCluster = new RoomCluster(grid, dungeon.gridWidth, dungeon.gridHeight)
        this.cellTypeTool.setGrid(grid)
        this.contentTool?.setGrid(grid)
        this.minimap?.setGrid(grid)
        this.energyMeter?.recalculateFromGrid(grid)
        this.refreshRoomList()

        // Focus camera on centroid
        const activeOcts = getActiveOctagons(grid)
        let sumX = 0, sumZ = 0
        for (const oct of activeOcts) {
            const wp = gridToWorld(oct.row, oct.col, dungeon.baseSquareSize, dungeon.cornerCutRatio)
            sumX += wp.x
            sumZ += wp.z
        }
        if (activeOcts.length > 0) {
            this.engine.focusOn(
                new Vector3(sumX / activeOcts.length, 0, sumZ / activeOcts.length),
                90 + activeOcts.length * 1.5
            )
        }

        this.updateDungeonInfo(activeOcts.length)
    }

    // ─── Room Clustering ─────────────────────────────

    private updateCreateRoomButton(cells: GridPos[]): void {
        const btn = document.getElementById('btnCreateRoom') as HTMLButtonElement
        if (!btn) return
        const valid = cells.length >= 2 && cells.length <= 6 && this.roomCluster !== null
        btn.disabled = !valid
        btn.textContent = valid ? `+ Create Room (${cells.length} cells)` : '+ Create Room'
    }

    private openRoomDialog(): void {
        console.log('[RoomCluster] openRoomDialog, pendingCells:', this.pendingRoomCells.length)
        if (this.pendingRoomCells.length < 2) return
        const dialog = document.getElementById('roomDialog') as HTMLDialogElement
        if (!dialog) {
            console.error('[RoomCluster] dialog element not found')
            return
        }

        const nameInput = document.getElementById('roomDialogName') as HTMLInputElement
        const typeSelect = document.getElementById('roomDialogType') as HTMLSelectElement
        const countEl = document.getElementById('roomDialogCellCount')

        if (nameInput) nameInput.value = ''
        if (typeSelect) typeSelect.value = 'custom'
        if (countEl) countEl.textContent = `${this.pendingRoomCells.length} cells selected`

        dialog.showModal()
        console.log('[RoomCluster] dialog opened')
        setTimeout(() => nameInput?.focus(), 50)
    }

    private handleCreateRoom(): void {
        if (!this.roomCluster) return

        const dialog = document.getElementById('roomDialog') as HTMLDialogElement
        const nameInput = document.getElementById('roomDialogName') as HTMLInputElement
        const typeSelect = document.getElementById('roomDialogType') as HTMLSelectElement

        const name = nameInput?.value.trim() || 'Unnamed Room'
        const type = (typeSelect?.value || 'custom') as RoomType

        console.log('[RoomCluster] createRoom:', name, type, this.pendingRoomCells)
        const result = this.roomCluster.createRoom(this.pendingRoomCells, name, type)
        dialog?.close()

        if (!result) {
            console.warn('[RoomCluster] createRoom failed — cells not contiguous or already in room')
            this.updateStatus('Cannot create room — cells must be adjacent and contiguous')
            return
        }

        // Update wall visuals
        for (const wc of result.wallChanges) {
            this.renderer.updateWall(wc.row, wc.col, wc.wallIndex, wc.state)
        }

        this.refreshRoomList()
        this.gridView.deselectAll()
        this.pendingRoomCells = []
        this.updateCreateRoomButton([])
        this.minimap?.render()
        this.updateStatus(`Created room "${result.room.name}" (${result.room.cells.length} cells)`)
    }

    private handleDissolveRoom(roomId: string): void {
        if (!this.roomCluster) return

        const room = this.roomCluster.getRoom(roomId)
        if (!room) return

        this.roomCluster.dissolveRoom(roomId)
        this.refreshRoomList()
        this.minimap?.render()
        this.updateStatus(`Dissolved room "${room.name}"`)
    }

    private refreshRoomList(): void {
        const listEl = document.getElementById('roomList')
        const badgeEl = document.getElementById('roomCountBadge')
        if (!listEl) return

        const rooms = this.roomCluster?.getRooms() ?? []
        if (badgeEl) badgeEl.textContent = String(rooms.length)

        if (rooms.length === 0) {
            listEl.innerHTML = ''
            return
        }

        const typeLabels: Record<string, string> = {
            custom: 'Custom',
            boss_arena: 'Boss',
            treasury: 'Treasury',
            shrine_complex: 'Shrine',
        }

        listEl.innerHTML = rooms.map(room => `
            <div class="room-item" data-room-id="${room.id}">
                <div class="room-item-info">
                    <span class="room-item-name">${room.name}</span>
                    <span class="room-item-meta">
                        ${room.cells.length} cells
                        <span class="room-type-badge">${typeLabels[room.type] || room.type}</span>
                    </span>
                </div>
                <button class="room-dissolve-btn" data-dissolve="${room.id}" title="Dissolve room">Dissolve</button>
            </div>
        `).join('')

        // Wire dissolve buttons
        listEl.querySelectorAll('[data-dissolve]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation()
                const id = (btn as HTMLElement).dataset.dissolve!
                this.handleDissolveRoom(id)
            })
        })

        // Wire room item click → highlight cells
        listEl.querySelectorAll('.room-item').forEach(item => {
            item.addEventListener('click', () => {
                const id = (item as HTMLElement).dataset.roomId!
                const room = this.roomCluster?.getRoom(id)
                if (!room) return
                this.renderer.deselectAll()
                for (const cell of room.cells) {
                    this.renderer.selectCell(cell.row, cell.col)
                }
                this.updateStatus(`Room "${room.name}" — ${room.cells.length} cells`)
            })
        })
    }

    // ─── Panel Focus ─────────────────────────────────

    private focusPropertiesPanel(open: boolean): void {
        const collapsible = ['dungeonInfoContent', 'modeGuideContent']
        const propsId = 'propertiesContent'

        for (const id of collapsible) {
            const content = document.getElementById(id)
            const toggle = content?.previousElementSibling as HTMLElement | null
            if (!content || !toggle) continue

            if (open) {
                toggle.classList.remove('active')
                content.classList.remove('open')
            } else {
                toggle.classList.add('active')
                content.classList.add('open')
            }
        }

        const propsContent = document.getElementById(propsId)
        const propsToggle = propsContent?.previousElementSibling as HTMLElement | null
        if (propsContent && propsToggle) {
            if (open) {
                propsToggle.classList.add('active')
                propsContent.classList.add('open')
            }
        }
    }

    private updateStatus(msg: string): void {
        const el = document.getElementById('statusText')
        if (el) el.textContent = msg
    }

    private updateDungeonInfo(roomCount: number): void {
        if (!this.dungeon) return

        // DungeonConfig panel handles all rendering
        this.dungeonConfig?.setDungeon(this.dungeon, roomCount)

        // Run validator
        this.runValidation()
    }

    private runValidation(): void {
        if (!this.dungeon || !this.validatorPanel) return
        const grid = this.dungeon.layers[0].grid
        this.validatorPanel.validate(grid, this.dungeon.entrance)
    }
}

// ─── Bootstrap ──────────────────────────────────────────────

const app = new DungeonMasterApp()
app.init().catch(err => {
    console.error('Failed to initialize Dungeon Master:', err)
})
